// client/src/components/Parent/PerformancePanel.js
//
// A child's results, ONE SEQUENCE AT A TIME.
//
// This used to be a matrix: subjects down the side, every sequence of the year
// across the top. By the third sequence it needed a horizontal scrollbar on a
// laptop, and it was never usable on a phone — six numeric columns beside a
// subject name do not fit in 360 pixels. Worse, it invited the wrong reading:
// a parent scanning a row of six numbers cannot see which term they belong to.
//
// So the sequence is CHOSEN and the table shows that one sequence for every
// subject, which is the question a parent actually asks ("how did they do in
// the second sequence?"). The averages above it still cover the whole year.
//
// Shared by the academics page and the dashboard's performance tab so the two
// cannot drift — they were separate copies of the same table before.
//
// All the arithmetic is in client/src/utils/parentAverages.generated.js, which
// is generated from packages/core so the phone computes it identically. Nothing
// here does maths.

import React, { useMemo, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert, Box, Chip, FormControl, InputLabel, MenuItem, Select, Table, TableBody,
  TableCell, TableHead, TableRow, Typography,
} from '@mui/material';
import {
  computeAverages, subjectsForSequence, gradeFor, DEFAULT_PASS_MARK,
} from '../../utils/parentAverages.generated';

const fmt = (n) => (n == null || !Number.isFinite(Number(n)) ? '—' : Number(n).toFixed(2));

export default function PerformancePanel({ subjects, marks, grading }) {
  const { t } = useTranslation();

  const passMark = Number(grading?.passMark) || DEFAULT_PASS_MARK;
  const scale = Array.isArray(grading?.scale) ? grading.scale : [];

  const averages = useMemo(
    () => computeAverages(marks, {
      subjects,
      termsPerYear: grading?.termsPerYear,
      sequencesPerTerm: grading?.sequencesPerTerm,
    }),
    [marks, subjects, grading?.termsPerYear, grading?.sequencesPerTerm]
  );

  const { sequenceNumbers } = averages;

  // Opens on the LATEST sequence, not the first: the one a parent is checking
  // is almost always the one just released.
  const [selected, setSelected] = useState(null);
  useEffect(() => {
    if (!sequenceNumbers.length) { setSelected(null); return; }
    setSelected((held) => (sequenceNumbers.includes(held) ? held : sequenceNumbers[sequenceNumbers.length - 1]));
  }, [sequenceNumbers]);

  const rows = useMemo(
    () => (selected == null ? [] : subjectsForSequence(subjects, marks, selected)),
    [subjects, marks, selected]
  );

  if (!sequenceNumbers.length) {
    return <Alert severity="info">{t('parentAcademics.noSequenceMarks')}</Alert>;
  }

  return (
    <>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
        {sequenceNumbers.map((sn) => (
          <Chip
            key={`seq-avg-${sn}`}
            size="small"
            variant={sn === selected ? 'filled' : 'outlined'}
            color={sn === selected ? 'primary' : 'default'}
            onClick={() => setSelected(sn)}
            label={t('parentAcademics.averages.sequence', { number: sn, value: fmt(averages.sequenceAverages[sn]) })}
          />
        ))}
        {/* Only terms that HAVE an average. A term is two sequences; with one
            of them marked there is no term result yet, and a "Term 1: —" chip
            invites the reader to treat a missing figure as a bad one. */}
        {averages.termNumbers.filter((tn) => averages.termAverages[tn] != null).map((tn) => (
          <Chip
            key={`term-avg-${tn}`}
            size="small"
            variant="outlined"
            label={t('parentAcademics.averages.term', { number: tn, value: fmt(averages.termAverages[tn]) })}
          />
        ))}
        {/* Present ONLY when the year is complete. The panel used to show an
            "annual average" built from whatever terms existed, so a child was
            reported on term one alone for most of the year. */}
        {averages.annualAverage != null && (
          <Chip
            size="small"
            color="primary"
            variant="outlined"
            label={t('parentAcademics.averages.annual', { value: fmt(averages.annualAverage) })}
          />
        )}
      </Box>

      {averages.annualAverage == null && averages.termsWithAverage > 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {t('parentAcademics.annualPending', {
            done: averages.termsWithAverage,
            total: averages.termsPerYear,
            defaultValue: 'The annual average appears once all {{total}} terms have results ({{done}} so far).',
          })}
        </Alert>
      )}

      <FormControl size="small" sx={{ minWidth: 220, mb: 2 }}>
        <InputLabel id="parent-sequence-label">{t('parentAcademics.chooseSequence', { defaultValue: 'Sequence' })}</InputLabel>
        <Select
          labelId="parent-sequence-label"
          value={selected ?? ''}
          label={t('parentAcademics.chooseSequence', { defaultValue: 'Sequence' })}
          onChange={(e) => setSelected(Number(e.target.value))}
        >
          {/* THE OPTION IS A NAME, NOT A RESULT. The average used to ride on
              the label, which put the same number in three places — chip, option
              and footer row — and made a list of choices read like a list of
              marks. The chips above are where the averages belong. */}
          {sequenceNumbers.map((sn) => (
            <MenuItem key={sn} value={sn}>
              {t('parentAcademics.sequenceShort', { number: sn })}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <Table size="small" sx={{ mb: 2 }}>
        <TableHead>
          <TableRow>
            <TableCell>{t('parentAcademics.columns.subjects')}</TableCell>
            <TableCell align="right">{t('parentAcademics.columns.coefficient', { defaultValue: 'Coef' })}</TableCell>
            <TableCell align="right">{t('parentAcademics.columns.score', { defaultValue: 'Score' })}</TableCell>
            <TableCell>{t('parentAcademics.columns.grade', { defaultValue: 'Grade' })}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => {
            const failed = row.score != null && row.score < passMark;
            const band = gradeFor(row.score, scale);
            return (
              <TableRow key={row.id}>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2">{row.name}</Typography>
                    {row.code ? <Chip size="small" label={row.code} /> : null}
                  </Box>
                </TableCell>
                <TableCell align="right">{row.coefficient}</TableCell>
                <TableCell align="right" sx={failed ? { color: 'error.main', fontWeight: 600 } : undefined}>
                  {fmt(row.score)}
                </TableCell>
                <TableCell>{band?.grade || '—'}</TableCell>
              </TableRow>
            );
          })}
          {rows.length === 0 && (
            <TableRow><TableCell colSpan={4}>{t('parentAcademics.noSubjects')}</TableCell></TableRow>
          )}
          <TableRow>
            <TableCell sx={{ fontWeight: 700 }}>{t('parentAcademics.average')}</TableCell>
            <TableCell />
            <TableCell align="right" sx={{ fontWeight: 700 }}>
              {fmt(selected == null ? null : averages.sequenceAverages[selected])}
            </TableCell>
            <TableCell sx={{ fontWeight: 700 }}>
              {selected != null && averages.sequenceAverages[selected] != null
                ? (gradeFor(averages.sequenceAverages[selected], scale)?.grade || '')
                : ''}
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </>
  );
}
